import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BlogCommentEntity,
  ServerListResponse,
  ServerSingleResponse,
} from "../../types";
import { AxiosResponse } from "axios";

type CommentPostPromise = Promise<
  AxiosResponse<ServerSingleResponse<BlogCommentEntity>>
>;
type UseEditCommentsonfigProps = (data: {
  body: string;
  comment: BlogCommentEntity;
}) => CommentPostPromise;

export function useEditComment(
  queryKey: QueryKey,
  onEditComment: UseEditCommentsonfigProps,
) {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: ({
      body,
      comment,
    }: {
      body: string;
      comment: BlogCommentEntity;
    }) =>
      onEditComment({
        body,
        comment,
      }),

    onMutate: ({ body, comment }) => {
      const prevState = queryClient.getQueryData(queryKey) as AxiosResponse<
        ServerListResponse<BlogCommentEntity>
      >;
      const prevReleaseCommentsData = prevState.data.payload.results;

      const newComment: BlogCommentEntity = {
        ...comment,
        body: body ?? "",
        parsed_body: `${body ?? ""}`,
      };

      const newState = {
        ...prevState,
        data: {
          payload: {
            results: editComment(prevReleaseCommentsData, newComment),
          },
        },
      };

      queryClient.setQueryData(queryKey, newState);

      return newState;
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(queryKey, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return mutate;
}

function editComment(
  comments: BlogCommentEntity[],
  updatedComment: BlogCommentEntity,
): BlogCommentEntity[] {
  return comments.map((comment) => {
    if (comment.id === updatedComment.id) {
      return { ...comment, ...updatedComment };
    }

    if (comment.replies?.length) {
      return {
        ...comment,
        replies: editComment(comment.replies, updatedComment),
      };
    }

    return comment;
  });
}
